import React, {useContext, useState, useEffect, useRef} from "react";
import {Form, Select} from "antd";
import {BrandContext} from "@context/BrandContext";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import {
    getUserSyllabusGroups,
    getUserConcepts,
    getUserTopics,
    getSyllabusesByUserBrandGroup,
    getUserTopicGroups,
    getUserTopicsByTopicGroup
} from "../axiosRequests/axiosRequests";

import {SELECT_LEVELS} from "@common/SyllabusTopicConceptSelect";
import {formatYearText, isObjectEmpty, objectHasEntry} from "@common/utilityFunctions";
import {UserContext} from "@context/UserContext";
import {ContentContext} from "@context/ContentContext";

/* If select_levels are used outside of TestEdit.js and select_levels default is different,
    useEffects logic for syllabusGroup/syllabus/topic/concept useStates needs to be updated */
/* This component is a different version of SyllabusTopicConceptSelect.js for Teacher Test Editor TestEdit.js  */

const TestContentSelect = props => {
    const {
        form,
        selectLevel = SELECT_LEVELS.CONCEPT,
        defaultLevel = SELECT_LEVELS.CONCEPT,
        onChange,
        disabled = false,
        popupMatchSelectWidth= false,
        onLoadComplete,
        searchSkillData,
        axiosProps = {},
        useStrands = false
    } = props;
    const brandConfig = useContext(BrandContext);
    const [syllabusGroupLoading, setSyllabusGroupLoading] = useState(false),
        [syllabusLoading, setSyllabusLoading] = useState(false),
        [topicGroupLoading, setTopicGroupLoading] = useState(false),
        [topicLoading, setTopicLoading] = useState(false),
        [conceptLoading, setConceptLoading] = useState(false),
        [initialised, setInitialised] = useState(false),
        [syllabusGroupList, setSyllabusGroupList] = useState([]),
        [topicGroupList, setTopicGroupList] = useState([]),
        [syllabusGroup, setSyllabusGroup] = useState({}),
        [topicGroup, setTopicGroup] = useState({topicGroupId:0}),
        [syllabusGroupId, setSyllabusGroupId] = useState(null),
        [syllabusList, setSyllabusList] = useState([]),
        [syllabus, setSyllabus] = useState({}),
        [topicList, setTopicList] = useState([]),
        [topic, setTopic] = useState({}),
        [conceptList, setConceptList] = useState([]),
        [concept, setConcept] = useState({}),
        syllabusGroupRef = useRef(null),
        syllabusRef = useRef(null),
        lastKeyPressed = useRef(null),
        lastRef = useRef(null),
        {user} = useContext(UserContext),
        {content} = useContext(ContentContext);

    useEffect(() => {
        if(!isObjectEmpty(axiosProps)) {
            if (!axiosProps.forDiagnostics) {
                setSyllabusGroupLoading(true);
                getUserSyllabusGroups(axiosProps).then((data) => {
                    setSyllabusGroupLoading(false);
                    setSyllabusGroupList(data);
                    if (data.length > 0) {
                        const initialSyllabusGroup = objectHasEntry(searchSkillData) ? data.find(sg => sg.syllabusGroupId === searchSkillData.syllabusGroupId) : (data.find(sg => sg.syllabusGroupId === content.currentSyllabusGroup?.id) || data[0]);
                        setSyllabusGroup(initialSyllabusGroup);
                        form.setFieldValue('syllabusGroupId', initialSyllabusGroup.syllabusGroupId);
                    }
                })
            } else {
                let params = {syllabusGroupId: user.syllabusGroupId, ...axiosProps};
                if(useStrands){
                    params.syllabusType = 'CURRICULUM';
                    params.hasCurriculumLink = false;
                }
                setSyllabusLoading(true);
                getSyllabusesByUserBrandGroup(params).then((data) => {
                    setSyllabusLoading(false);
                    setSyllabusList(data);
                    if(data.length > 0){
                        const initialSyllabus = objectHasEntry(searchSkillData) ? data.find(s => s.syllabusId === searchSkillData.syllabusId) : (data.find(sg => sg.syllabusId === content.currentSyllabus?.syllabusId) || data[0]);
                        setSyllabus(initialSyllabus);
                        form.setFieldValue('syllabusId', initialSyllabus.syllabusId);
                    }
                });
            }
        }
    }, [axiosProps]);

    useEffect(()=>{
        if(!isObjectEmpty(syllabusGroup)){
            setSyllabusGroupId(syllabusGroup.syllabusGroupId);
            if(syllabusGroup.syllabuses && syllabusGroup.syllabuses.length > 0){
                setSyllabusList(syllabusGroup.syllabuses);

                const initialSyllabus = objectHasEntry(searchSkillData) ? syllabusGroup.syllabuses.find(s => s.syllabusId === searchSkillData.syllabusId) : (syllabusGroup.syllabuses.find(sg => sg.syllabusId === content.currentSyllabus?.syllabusId) || syllabusGroup.syllabuses[0]);

                setSyllabus(initialSyllabus);
                form.setFieldValue('syllabusId',initialSyllabus.syllabusId);
            }
        }
        if(syllabusGroup?.fromSelect){
            onChange?.({syllabusGroupId:syllabusGroup.syllabusGroupId});
        }
    },[syllabusGroup]);

    useEffect(()=>{
        if(syllabus.hasOwnProperty('syllabusId')){
            if(syllabus.syllabusId === 0){
                setTopicList([]);
                setTopic({topicId: 0});
                form.setFieldValue('topicId',0);
            }else{
                if(useStrands){
                    setTopicGroupLoading(true);
                    getUserTopicGroups({syllabusId: syllabus.syllabusId, ...axiosProps}).then((data)=>{
                        setTopicGroupLoading(false);
                        setTopicGroupList(data);
                        if(data.length > 0) {
                            const initialTopicGroup = objectHasEntry(searchSkillData) ? data.find(tg => tg.topicGroupId === searchSkillData.topicGroupId) : (data.find(sg => sg.syllabusId === content.currentTopicGroup?.id) || data[0]);
                            setTopicGroup(initialTopicGroup);
                            form.setFieldValue('topicGroupId', initialTopicGroup.topicGroupId);
                        }
                    });
                }else{
                    setTopicLoading(true);
                    getUserTopics({syllabusId:syllabus.syllabusId, ...axiosProps}).then(data => {
                        setTopicLoading(false);
                        setTopicList(data);

                        const initialTopic = objectHasEntry(searchSkillData) ? data.find(t => t.topicId === searchSkillData.topicId) : (data.find(sg => sg.topicId === content.currentTopic?.topicId) || data[0]);

                        setTopic(initialTopic);
                        form.setFieldValue('topicId',initialTopic.topicId);
                    })
                }
            }
            if(syllabus?.fromSelect){
                onChange?.({syllabusId:syllabus.syllabusId});
            }
        }
    },[syllabus]); // searchSkillData intentionally excluded

    useEffect(()=>{
        if(topic.hasOwnProperty('topicId')){
            if(topic.topicId === 0){
                setConceptList([]);
                setConcept({conceptId:0});
                form.setFieldValue('conceptId',0);
            }else{
                setConceptLoading(true);
                getUserConcepts({topicId: topic.topicId, ...axiosProps}).then(data =>{
                    setConceptLoading(false);
                    setConceptList(data);
                    const initialConcept = objectHasEntry(searchSkillData) ? data.find(c => c.conceptId === searchSkillData.conceptId) :  (data.find(sg => sg.conceptId === content.currentConcept?.conceptId) || {conceptId: 0});
                    setConcept(initialConcept);
                    setInitialised(true);
                    form.setFieldValue('conceptId', initialConcept.conceptId);
                })
            }
            if(topic?.fromSelect){
                onChange?.({topicId:topic.topicId});
            }
        }
    },[topic])

    useEffect(()=>{
        if(initialised && concept.hasOwnProperty('conceptId')){
            onChange?.({conceptId:concept.conceptId, fromSelect:concept.fromSelect});
        }
    },[concept]);

    useEffect(()=>{
        if(objectHasEntry(topicGroup) && topicGroup.hasOwnProperty('topicGroupId')){
            if(topicGroup.topicGroupId === 0){
                setTopicList([]);
                setTopic({topicId:0});
                form.setFieldValue('topicId',0);
            }else{
                setTopicLoading(true);
                getUserTopicsByTopicGroup({topicGroupId: topicGroup.topicGroupId, ...axiosProps}).then(data =>{
                    setTopicLoading(false);
                    setTopicList(data);

                    const initialTopic = objectHasEntry(searchSkillData) ? data.find(t => t.topicId === searchSkillData.topicId) :  (data.find(sg => sg.topicId === content.currentTopic?.topicId) || data[0]);

                    setTopic(initialTopic);
                    form.setFieldValue('topicId',initialTopic.topicId);
                })
            }
            if(topicGroup?.fromSelect){
                onChange?.();
            }
        }
    },[topicGroup]);

    useEffect(() => {
        if (objectHasEntry(searchSkillData)) {
            const currentSgId = form.getFieldValue('syllabusGroupId');
            const newSgId = searchSkillData.syllabusGroupId;
            if (newSgId && newSgId !== currentSgId) {
                const newSyllabusGroup = syllabusGroupList.find(sg => sg.syllabusGroupId === newSgId);
                if (newSyllabusGroup) {
                    setSyllabusGroup({...newSyllabusGroup, fromSelect: true});
                    form.setFieldValue('syllabusGroupId', newSgId);
                }
            }
        }
    }, [searchSkillData, syllabusGroupList]);

    const updateSelects = (obj)=>{
        if(obj.hasOwnProperty('syllabusGroupId')){
            if(obj.syllabusGroupId === 0){
                setSyllabusGroup({syllabusGroupId:0, fromSelect:true});
            }else{
                setSyllabusGroup({fromSelect: true, ...(syllabusGroupList.find(syllabusGroup => syllabusGroup.syllabusGroupId === obj.syllabusGroupId))});
            }
        }else if(obj.hasOwnProperty('syllabusId')){
            if(obj.syllabusId === 0){
                setSyllabus({syllabusId:0, fromSelect:true});
            }else {
                setSyllabus({fromSelect: true, ...(syllabusList.find(syllabus => syllabus.syllabusId === obj.syllabusId))});
            }
        }else if(obj.hasOwnProperty('topicGroupId')){
            if(obj.topicGroupId === 0){
                setTopicGroup({topicGroupId:0, fromSelect:true});
            }else {
                setTopicGroup({fromSelect: true, ...(topicGroupList.find(topicGroup => topicGroup.topicGroupId === obj.topicGroupId))});
            }
        }else if(obj.hasOwnProperty('topicId')){
            if(obj.topicId === 0){
                setTopic({topicId:0, fromSelect:true});
            }else {
                setTopic({fromSelect: true, ...(topicList.find(topic => topic.topicId === obj.topicId))});
            }
        }else if(obj.hasOwnProperty('conceptId')){
            if(obj.conceptId === 0){
                setConcept({conceptId:0, fromSelect:true});
            }else {
                setConcept({fromSelect: true, ...(conceptList.find(concept => concept.conceptId === obj.conceptId))});
            }
        }
    }

    return (
        <>
            {!axiosProps.forDiagnostics && <Form.Item
                label={`${brandConfig.syllabusGroupName}` + (syllabusGroupList.length > 0 ? ` (${syllabusGroupList.length})`:'')}
                name="syllabusGroupId"
            >
                <Select
                    popupMatchSelectWidth={popupMatchSelectWidth}
                    ref={syllabusGroupRef}
                    onKeyDownCapture={(e) => {
                        lastKeyPressed.current = e.code;
                    }}
                    onClick={()=>{lastKeyPressed.current = null;}}
                    onSelect={()=>{lastRef.current = syllabusGroupRef.current;}}
                    onChange={syllabusGroupId => updateSelects({syllabusGroupId: syllabusGroupId})}
                    virtual={false}
                    showSearch={false}
                    loading={syllabusGroupLoading}
                    value={syllabusGroupId}
                    disabled={disabled || syllabusGroupLoading}
                >
                    {defaultLevel === SELECT_LEVELS.NONE &&
                        <Select.Option value={0}>All</Select.Option>
                    }
                    {syllabusGroupList?.map(syllabusGroup => (
                        <Select.Option key={syllabusGroup.syllabusGroupId} value={syllabusGroup.syllabusGroupId}>{syllabusGroup.syllabusGroupName}</Select.Option>
                    ))}
                </Select>
            </Form.Item>
            }
            {(((selectLevel === SELECT_LEVELS.SYLLABUS || selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && !useStrands) || (useStrands && syllabusList.length > 1)) && (
                <Form.Item
                    label={(useStrands ? 'School year' : brandConfig.syllabusName) + (syllabusList.length > 0 ? ` (${syllabusList.length})`:'')}
                    name="syllabusId"
                    disabled={syllabusList.length === 1}
                >
                    <Select
                        popupMatchSelectWidth={popupMatchSelectWidth}
                        style={{maxWidth:window.innerWidth}}
                        ref={syllabusRef}
                        onKeyDownCapture={(e) => {
                            lastKeyPressed.current = e.code;
                        }}
                        onClick={()=>{lastKeyPressed.current = null;}}
                        onSelect={()=>{lastRef.current = syllabusRef.current;}}
                        onChange={syllabusId => updateSelects({syllabusId: syllabusId})}
                        virtual={false}
                        showSearch={false}
                        loading={syllabusLoading}
                        disabled={disabled || syllabusLoading}
                    >
                        {defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {syllabusList?.map(syllabus => (
                            <Select.Option key={syllabus.syllabusId} value={syllabus.syllabusId}>{useStrands ? formatYearText(syllabus.schoolYear) : syllabus.syllabusName}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
            {useStrands && (
                <Form.Item
                    label={`${brandConfig.curriculumTopicGroupName}` + (topicGroupList.length > 0 ? ` (${topicGroupList.length})` : '')}
                    name="topicGroupId"
                >
                    <Select
                        popupMatchSelectWidth={popupMatchSelectWidth}
                        style={{maxWidth:window.innerWidth}}
                        ref={syllabusRef}
                        onKeyDownCapture={(e) => {
                            lastKeyPressed.current = e.code;
                        }}
                        onClick={()=>{lastKeyPressed.current = null;}}
                        onSelect={()=>{lastRef.current = syllabusRef.current;}}
                        onChange={topicGroupId => updateSelects({topicGroupId: topicGroupId})}
                        virtual={false}
                        showSearch={false}
                        loading={topicGroupLoading}
                        disabled={disabled || topicGroupLoading}
                    >
                        {topicGroupList?.map(topicGroup => (
                            <Select.Option key={topicGroup.topicGroupId} value={topicGroup.topicGroupId}>{topicGroup.topicGroupName}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
            {(selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item
                    label={(useStrands ? brandConfig.curriculumTopicName : (axiosProps.forExams ? brandConfig.examAreaOfStudyName : brandConfig.topicName))+ (topicList.length > 0 ? ` (${topicList.length})` : '')}
                    name="topicId"
                >
                    <Select
                        popupMatchSelectWidth={popupMatchSelectWidth}
                        style={{maxWidth:window.innerWidth}}
                        onChange={topicId => updateSelects({topicId: topicId})}
                        virtual={false}
                        showSearch={false}
                        loading={topicLoading}
                        disabled={disabled || topicLoading}
                    >
                        {(defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {topicList.map(topic => (
                            <Select.Option key={topic.topicId} value={topic.topicId}>{topic.topicName}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
            {(selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item
                    label={(useStrands ? brandConfig.curriculumConceptName : brandConfig.conceptName) + (conceptList.length > 0 ?` (${conceptList.length})`:'')}
                    name="conceptId"
                >
                    <Select
                        popupMatchSelectWidth={popupMatchSelectWidth}
                        style={{maxWidth:window.innerWidth}}
                        onChange={conceptId => updateSelects({conceptId: conceptId})}
                        virtual={false}
                        showSearch={false}
                        loading={conceptLoading}
                        disabled={disabled || conceptLoading}
                    >
                        {(defaultLevel === SELECT_LEVELS.TOPIC || defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {conceptList?.map(concept => (
                            <Select.Option key={concept.conceptId} value={concept.conceptId}>{
                                useStrands && concept.hasOwnProperty('curriculumCode')
                                    ? concept.curriculumCode
                                    : <MathJaxHTMLContent html={concept.conceptName} />
                            }</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
        </>
    )
}

export default TestContentSelect;